import type {
  ApplicationId,
  NoteColor,
  ObjectId,
  Size,
  SpatialApplication,
  SpatialBookmark,
  SpatialFile,
  SpatialFolder,
  SpatialGroup,
  SpatialImage,
  SpatialNote,
  SpatialObject,
  SpatialObjectKind,
  SpatialTerminal,
  SpatialWindow,
  Vec2,
  WorkspaceId,
} from '@/types';
import { asObjectId } from '@/types';
import { transformAt } from '@/core/geometry/transform';
import { objectType } from '@/core/scene/objectTypes';
import { createId } from '@/utils/id';

interface BaseInput {
  readonly workspaceId: WorkspaceId;
  readonly position: Vec2;
  readonly zIndex: number;
  readonly size?: Size;
  readonly name?: string;
  readonly now?: number;
}

const metadata = (now: number) => ({ createdAt: now, updatedAt: now, tags: [] as readonly string[] });

/** Fields every kind shares, filled from the type definition's defaults. */
const base = (kind: SpatialObjectKind, input: BaseInput) => {
  const def = objectType(kind);
  const now = input.now ?? Date.now();
  return {
    id: asObjectId(createId('obj')),
    workspaceId: input.workspaceId,
    name: input.name ?? def.label,
    transform: transformAt(input.position),
    size: input.size ?? def.defaultSize,
    zIndex: input.zIndex,
    parentId: null,
    locked: false,
    visible: true,
    metadata: metadata(now),
  };
};

export const createNote = (
  input: BaseInput & { readonly content?: string; readonly color?: NoteColor },
): SpatialNote => ({
  ...base('note', input),
  kind: 'note',
  content: input.content ?? '',
  color: input.color ?? 'paper',
});

export const createWindow = (input: BaseInput & { readonly applicationId: ApplicationId }): SpatialWindow => ({
  ...base('window', input),
  kind: 'window',
  applicationId: input.applicationId,
  windowState: 'normal',
  applicationState: {},
  previousFrame: null,
});

export const createGroup = (input: BaseInput & { readonly childIds?: readonly ObjectId[] }): SpatialGroup => ({
  ...base('group', input),
  kind: 'group',
  childIds: input.childIds ?? [],
  collapsed: false,
  expandedSize: null,
});

export const createFolder = (input: BaseInput & { readonly childIds?: readonly ObjectId[] }): SpatialFolder => ({
  ...base('folder', input),
  kind: 'folder',
  childIds: input.childIds ?? [],
  collapsed: false,
  expandedSize: null,
});

export const createFile = (
  input: BaseInput & { readonly fileName?: string; readonly mimeType?: string; readonly byteSize?: number },
): SpatialFile => ({
  ...base('file', input),
  kind: 'file',
  fileName: input.fileName ?? 'untitled.txt',
  mimeType: input.mimeType ?? 'text/plain',
  byteSize: input.byteSize ?? 0,
});

export const createTerminal = (input: BaseInput & { readonly cwd?: string }): SpatialTerminal => ({
  ...base('terminal', input),
  kind: 'terminal',
  cwd: input.cwd ?? '~',
  scrollback: [],
});

export const createBookmark = (
  input: BaseInput & { readonly url?: string; readonly title?: string; readonly description?: string },
): SpatialBookmark => ({
  ...base('bookmark', input),
  kind: 'bookmark',
  url: input.url ?? 'https://example.com',
  title: input.title ?? 'Example',
  description: input.description ?? '',
});

export const createImage = (input: BaseInput & { readonly src?: string; readonly alt?: string }): SpatialImage => ({
  ...base('image', input),
  kind: 'image',
  src: input.src ?? PLACEHOLDER_IMAGE,
  alt: input.alt ?? 'Placeholder image',
  naturalSize: null,
});

export const createApplication = (
  input: BaseInput & { readonly applicationId: ApplicationId },
): SpatialApplication => ({
  ...base('application', input),
  kind: 'application',
  applicationId: input.applicationId,
});

/** Generic factory used by the "Add" menu; kinds needing an application id receive one. */
export const createObjectOfKind = (
  kind: SpatialObjectKind,
  input: BaseInput & { readonly applicationId: ApplicationId },
): SpatialObject => {
  switch (kind) {
    case 'note':
      return createNote(input);
    case 'window':
      return createWindow(input);
    case 'group':
      return createGroup(input);
    case 'folder':
      return createFolder(input);
    case 'file':
      return createFile(input);
    case 'terminal':
      return createTerminal(input);
    case 'bookmark':
      return createBookmark(input);
    case 'image':
      return createImage(input);
    case 'application':
      return createApplication(input);
  }
};

/** A tiny inline SVG so new images render without any network access. */
export const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 240"><rect width="320" height="240" fill="#cfd8b8"/><circle cx="96" cy="88" r="28" fill="#f9f6ef"/><path d="M0 240 L110 130 L170 190 L230 140 L320 240 Z" fill="#8d8578"/></svg>',
  );
